import { ErrorHandler, inject, Injectable, NgZone } from "@angular/core";

import { LoggerService } from "@core/services/logger.service";

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly _logger = inject(LoggerService);
  private readonly _zone = inject(NgZone);

  // Track errors we've already handled to prevent duplicates
  private readonly _handledErrors = new WeakSet<object>();

  constructor() {
    this.setupGlobalErrorHandlers();
  }

  handleError(error: Error): void {
    this.handleErrorInternal(error);
  }

  /**
   * Setup global error handlers for JavaScript runtime errors and unhandled promise rejections
   */
  private setupGlobalErrorHandlers(): void {
    // Handle JavaScript runtime errors
    window.onerror = (message: string | Event, source?: string, lineno?: number, colno?: number, error?: Error): boolean => {
      this.handleErrorInternal(error || new Error(String(message)), {
        source,
        lineno,
        colno,
        type: "JavaScript Runtime Error"
      });
      return false; // Don't prevent default browser error handling
    };

    // Handle unhandled promise rejections
    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent): void => {
      this.handleErrorInternal(event.reason || new Error("Unhandled promise rejection"), {
        type: "Unhandled Promise Rejection"
      });
      event.preventDefault(); // Prevent the default browser behavior
    });

    // Handle resource loading errors (images, scripts, etc.)
    window.addEventListener(
      "error",
      (event: ErrorEvent): void => {
        if (event.target !== window) {
          this.handleErrorInternal(new Error(`Resource failed to load: ${event.target}`), {
            type: "Resource Loading Error",
            target: event.target
          });
        }
      },
      true
    );
  }

  /**
   * Internal error handling logic
   */
  private handleErrorInternal(error: any, context?: any): void {
    // Extract the error information
    const extractedError = this.extractError(error);

    // Skip if we've already handled this error instance
    if (extractedError instanceof Object && this._handledErrors.has(extractedError)) {
      return;
    }

    // Mark this error as handled
    if (extractedError instanceof Object) {
      this._handledErrors.add(extractedError);
    }

    const errorMessage = this.getErrorMessage(extractedError);
    const contextInfo = context ? ` (${JSON.stringify(context)})` : "";

    // Use NgZone to ensure the error is logged in the Angular zone
    this._zone.run(() => {
      // Log the error with context and original error object to preserve stack trace
      this._logger.error(`Unhandled error${contextInfo}: ${errorMessage}`, extractedError);
    });
  }

  /**
   * Extract the original error from potential wrapper errors
   */
  private extractError(error: any): any {
    // Unwrap zone.js error
    if (error?.ngOriginalError) {
      error = error.ngOriginalError;
    }

    // Get original error from Promises, Observables, etc.
    if (error?.rejection) {
      error = error.rejection;
    }

    return error;
  }

  /**
   * Get a readable error message
   */
  private getErrorMessage(error: any): string {
    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === "string") {
      return error;
    }

    if (error?.message) {
      return error.message;
    }

    return JSON.stringify(error);
  }
}
